if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.boundSubmitHandler = this.onSubmitHandler.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('form');

        if (!this.form) return;
        this.form.addEventListener('submit', this.boundSubmitHandler);

        if (this.form.querySelector('[name=id]')) {
          this.form.querySelector('[name=id]').disabled = false;
        }

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      disconnectedCallback() {
        if (this.form) {
          this.form.removeEventListener('submit', this.boundSubmitHandler);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // Add-to-cart goes through the stacked app fetch wrappers so gift-with-
        // purchase and subscription apps still see it. When one returns a
        // non-Response, the add dies with "Invalid response from server" (Noibu
        // #9687, ~73% Safari/iOS). Retry ONCE through the pristine, unwrapped
        // fetch: the add most likely never reached Shopify (a wrapper
        // short-circuited), so the retry completes it. Worst case the item lands
        // in the cart twice — visible and correctable before checkout — far
        // better than a dead add-to-bag button. The shopper-facing error text is
        // unchanged and only shows if BOTH attempts fail.
        const requestAdd = (fetchImpl) => fetchImpl(`${routes.cart_add_url}`, config);

        requestAdd(window.fetch)
          .then((response) => {
            // Safari safety check: ensure response is valid and has json method.
            if (response && typeof response.json === 'function') return response;

            if (window.reportRequestError) {
              window.reportRequestError({ operation: 'cart:add', url: routes.cart_add_url, variantId: formData.get('id'), reason: 'response-not-a-Response', detail: response });
            } else {
              console.error('Invalid response object:', response);
            }

            // Verify before replaying: a wrapper may have already sent the add
            // and THEN returned a non-Response, so blindly retrying could
            // double-add. Re-read the cart and retry ONLY when the variant is
            // ABSENT (the add definitely didn't land — the common case, a wrapper
            // short-circuit). If the variant is already present we do NOT retry.
            // Deliberate tradeoff: we can't distinguish "just added" from "was
            // already in the cart" without a pre-add snapshot, and a snapshot
            // would either add a round-trip to EVERY add or (fired in parallel)
            // race the write and reintroduce the double-add. So on a same-variant
            // re-add we accept a rare, safe UNDER-add (shopper re-clicks) rather
            // than ever over-adding. Safe direction: this never double-adds.
            const pristine = (window.credoPristineFetch && window.credoPristineFetch()) || window.fetch;
            const addedVariantId = String(formData.get('id'));
            return pristine('/cart.js')
              .then((r) => (r && typeof r.json === 'function') ? r.json() : null)
              .catch(() => null)
              .then((cart) => {
                // An unknown cart state cannot prove that the first request
                // was not applied. Never replay an add after a failed or
                // malformed confirmation read; doing so can double-add.
                if (!cart || !Array.isArray(cart.items)) {
                  if (window.reportRequestError) {
                    window.reportRequestError({ operation: 'cart:add', url: '/cart.js', variantId: formData.get('id'), reason: 'cart-confirmation-failed' });
                  }
                  throw new Error('Unable to confirm cart state; add was not retried.');
                }
                const inCart = cart && Array.isArray(cart.items) && cart.items.some(
                  (it) => String(it.id) === addedVariantId || String(it.variant_id) === addedVariantId
                );
                if (inCart) {
                  // Already applied — do NOT replay. Handled in .catch: refresh
                  // the cart UI, no error, no double-add.
                  throw { credoAddApplied: true };
                }
                return requestAdd(pristine).then((retry) => {
                  if (retry && typeof retry.json === 'function') return retry;
                  if (window.reportRequestError) {
                    window.reportRequestError({ operation: 'cart:add', url: routes.cart_add_url, variantId: formData.get('id'), reason: 'response-not-a-Response-after-retry', detail: retry });
                  }
                  throw new Error('Invalid response from server. Please try again.');
                });
              });
          })
          .then((response) => {
            // Check if response is OK, if not try to parse error
            if (!response.ok) {
              if (window.reportRequestError) {
                window.reportRequestError({ operation: 'cart:add', url: routes.cart_add_url, variantId: formData.get('id'), response, reason: 'http-error' });
              }
              // Try to get error message from response
              return response.text().then(text => {
                let errorData;
                try {
                  errorData = JSON.parse(text);
                } catch (e) {
                  // If response isn't JSON, create error object
                  errorData = {
                    status: response.status,
                    message: `HTTP error! Status: ${response.status}`,
                    description: text || `Failed to add item to cart. Status: ${response.status}`
                  };
                }
                throw errorData;
              });
            }
            
            // Check content type to ensure it's JSON (Safari can be strict)
            const contentType = response.headers.get('content-type');
            if (contentType && !contentType.includes('application/json') && !contentType.includes('text/javascript')) {
              return response.text().then(text => {
                throw {
                  status: response.status,
                  message: 'Invalid response format',
                  description: 'Server returned non-JSON response'
                };
              });
            }
            
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            // Non-Response add that had ALREADY applied (Noibu #9687): the item
            // is in the cart, so show no error and don't replay. Refresh the cart
            // sections so the drawer/bubble reflect the item that's there.
            if (e && e.credoAddApplied) {
              this.error = false;
              if (this.cart && typeof this.cart.getSectionsToRender === 'function' && typeof this.cart.renderContents === 'function') {
                const ids = this.cart.getSectionsToRender().map((s) => s.id).join(',');
                const pristine = (window.credoPristineFetch && window.credoPristineFetch()) || window.fetch;
                pristine(`${routes.cart_url}?sections=${ids}`)
                  .then((r) => (r && typeof r.json === 'function') ? r.json() : null)
                  .then((sections) => { if (sections) this.cart.renderContents({ sections }); })
                  .catch(() => {});
              }
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
              return;
            }
            console.error('Add to cart error:', e);
            // Handle error objects that were thrown
            if (e && typeof e === 'object' && (e.status || e.message || e.description)) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: e.errors || e.description,
                message: e.message || 'Failed to add item to cart',
              });
              this.handleErrorMessage(e.description || e.message || 'Failed to add item to cart. Please try again.');
              this.error = true;
            } else {
              // Generic error handling
              this.handleErrorMessage('Failed to add item to cart. Please try again.');
              this.error = true;
            }
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
